import React from "react";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Close from "@mui/icons-material/Close";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";

import { useFormik } from "formik";
import { Divider, IconButton, MenuItem } from "@mui/material";
import { TextFieldWithLabel } from "components/CustomTextField";
import { CustomLoadingButton } from "components/CustomButton";
import { useSnackbar } from "contexts/SnackbarProvider";
import { subscribeCaseMasterUser } from "services/caseMasterService";

const INTERVIEW_URL = process.env.REACT_APP_INTERVIEW_URL;

// Dropdown options for the profile fields captured during free-trial signup.
const ACADEMIC_YEARS = [
    'First Year',
    'Second Year',
    'Third Year',
    'Fourth Year',
    'Final Year',
    'MBA 1st Year',
    'MBA 2nd Year',
    'Other',
];

const ASPIRING_ROLES = [
    'Product Manager',
    'Consulting',
    'Business Analyst',
    'EIR',
    "Founder's Office",
    'Operations',
    'Others',
];

const REFERRAL_SOURCES = [
    'Whatsapp',
    'Instagram',
    'LinkedIn',
    'College Community',
    'Webinar',
    'Friend',
    'Other',
];

const useStyles = makeStyles((theme) => ({
    cardContainer: {
        display: "flex",
        width: '100%',
        alignItems: "left",
        gap: 20,
        flexDirection: 'column',
        marginTop: '30px',
        marginBottom: '20px',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: "center"
        },
    },
    submitButton: {
        textTransform: 'none',
        height: '45px',
        ...theme.typography['h6-medium'],
    },
    statusContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: theme.spacing(3),
        marginTop: '32px',
        marginBottom: '12px',
    },
    statusIcon: {
        fontSize: '56px',
        color: '#0A3F63',
    },
    statusEmail: {
        ...theme.typography['body01-bold'],
        color: theme.palette.neutral['clr-900'],
    },
}));

const FormValidationSchema = yup.object({
    name: yup.string()
        .required("Please enter your name")
        .min(2, "Name must be at least 2 characters")
        .trim(),
    email: yup.string()
        .email("Please enter a valid email")
        .required("Please enter your email")
        .trim(),
    whatsappNumber: yup.string()
        .required("Please enter your WhatsApp number")
        .test(
            'valid-phone',
            "Please enter a valid contact number",
            (val) => {
                const digits = (val || '').replace(/\D/g, '');
                return digits.length >= 10 && digits.length <= 15;
            }
        )
        .trim(),
    collegeName: yup.string()
        .required("Please enter your college name")
        .min(2, "Please enter a valid college name")
        .trim(),
    academicYear: yup.string()
        .required("Please select your academic year")
        .oneOf(ACADEMIC_YEARS, "Please select your academic year"),
    aspiringRole: yup.string()
        .required("Please select an aspiring role")
        .oneOf(ASPIRING_ROLES, "Please select an aspiring role"),
    referralSource: yup.string()
        .required("Please let us know how you found us")
        .oneOf(REFERRAL_SOURCES, "Please let us know how you found us"),
});

export default function CaseMasterSignupDialog({ open, onClose }) {
    const classes = useStyles();
    const snackbar = useSnackbar();

    const [loading, setLoading] = React.useState(false);
    // 'form' | 'success' | 'claimed'
    const [view, setView] = React.useState('form');
    const [submittedEmail, setSubmittedEmail] = React.useState('');

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            whatsappNumber: "",
            collegeName: "",
            academicYear: "",
            aspiringRole: "",
            referralSource: "",
        },
        validationSchema: FormValidationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                await subscribeCaseMasterUser({
                    name: values.name.trim(),
                    email: values.email.trim(),
                    contact: values.whatsappNumber.trim(),
                    plan: "free",
                    metadata: {
                        whatsappNumber: values.whatsappNumber.trim(),
                        collegeName: values.collegeName.trim(),
                        academicYear: values.academicYear,
                        aspiringRole: values.aspiringRole,
                        referralSource: values.referralSource,
                    },
                });

                setSubmittedEmail(values.email.trim());
                setView('success');
            } catch (error) {
                console.error(error);
                // 409: the email already has an account. No new credential email
                // is sent in this case, so point the user at login instead.
                if (error?.status === 409) {
                    setSubmittedEmail(values.email.trim());
                    setView('claimed');
                }

                snackbar.showSnackbar(
                    error?.message || "Something went wrong. Please try again.",
                    'error'
                );
            } finally {
                setLoading(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        setView('form');
        setSubmittedEmail('');
        onClose();
    };

    const goToLogin = () => {
        if (INTERVIEW_URL) window.open(INTERVIEW_URL, '_blank', 'noopener,noreferrer');
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" keepMounted>
            <DialogContent style={{ position: 'relative', borderRadius: '8px' }}>
                <IconButton
                    onClick={handleClose}
                    style={{ position: "absolute", top: 16, right: 15 }}
                >
                    <Close />
                </IconButton>

                {view === 'form' && (
                    <>
                        <Typography variant="h3-semiBold" component="div">
                            Start practicing for free
                        </Typography>
                        <Typography variant="body01-regular" component="div" color="neutral.clr-600" style={{ marginTop: '8px' }}>
                            Enter your details and we'll email your login credentials.
                        </Typography>
                        <Divider style={{ marginTop: '16px' }} />

                        <Box className={classes.cardContainer}>
                            <TextFieldWithLabel
                                id='name'
                                name='name'
                                label='Full name'
                                placeholder='Enter your full name'
                                required
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.name && !!formik.errors.name) ? formik.errors.name : ''}
                            />

                            <TextFieldWithLabel
                                id='email'
                                name='email'
                                label='Email address'
                                placeholder='example@gmail.com'
                                type='email'
                                required
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.email && !!formik.errors.email) ? formik.errors.email : ''}
                            />

                            <TextFieldWithLabel
                                id='whatsappNumber'
                                name='whatsappNumber'
                                label='Contact number'
                                supportingLabel='(WhatsApp)'
                                placeholder='e.g. +91 98765 43210'
                                type='tel'
                                required
                                value={formik.values.whatsappNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.whatsappNumber && !!formik.errors.whatsappNumber) ? formik.errors.whatsappNumber : ''}
                            />

                            <TextFieldWithLabel
                                id='collegeName'
                                name='collegeName'
                                label='College name'
                                placeholder='Enter your college name'
                                required
                                value={formik.values.collegeName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.collegeName && !!formik.errors.collegeName) ? formik.errors.collegeName : ''}
                            />

                            <TextFieldWithLabel
                                select
                                id='academicYear'
                                name='academicYear'
                                label='Academic year'
                                required
                                value={formik.values.academicYear}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.academicYear && !!formik.errors.academicYear) ? formik.errors.academicYear : ''}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value='' disabled>
                                    Select your academic year
                                </MenuItem>
                                {ACADEMIC_YEARS.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextFieldWithLabel>

                            <TextFieldWithLabel
                                select
                                id='aspiringRole'
                                name='aspiringRole'
                                label='Which job role are you aspiring for?'
                                required
                                value={formik.values.aspiringRole}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.aspiringRole && !!formik.errors.aspiringRole) ? formik.errors.aspiringRole : ''}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value='' disabled>
                                    Select a role
                                </MenuItem>
                                {ASPIRING_ROLES.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextFieldWithLabel>

                            <TextFieldWithLabel
                                select
                                id='referralSource'
                                name='referralSource'
                                label='How did you know about us?'
                                required
                                value={formik.values.referralSource}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(formik.touched.referralSource && !!formik.errors.referralSource) ? formik.errors.referralSource : ''}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value='' disabled>
                                    Select an option
                                </MenuItem>
                                {REFERRAL_SOURCES.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextFieldWithLabel>

                            <CustomLoadingButton
                                onClick={formik.handleSubmit}
                                type="button"
                                className={classes.submitButton}
                                loading={loading}
                            >
                                Get my free case
                            </CustomLoadingButton>
                        </Box>
                    </>
                )}

                {view === 'success' && (
                    <Box className={classes.statusContainer}>
                        <MarkEmailReadOutlinedIcon className={classes.statusIcon} />
                        <Typography variant="h3-semiBold">You're all set!</Typography>
                        <Typography variant="body01-regular" color="neutral.clr-600">
                            We've sent your login details to{' '}
                            <span className={classes.statusEmail}>{submittedEmail}</span>.
                            Check your inbox (and spam folder) to log in and start practising.
                        </Typography>
                        {INTERVIEW_URL && (
                            <CustomLoadingButton
                                onClick={goToLogin}
                                type="button"
                                className={classes.submitButton}
                                style={{ width: '100%' }}
                            >
                                Go to login
                            </CustomLoadingButton>
                        )}
                    </Box>
                )}

                {view === 'claimed' && (
                    <Box className={classes.statusContainer}>
                        <MarkEmailReadOutlinedIcon className={classes.statusIcon} />
                        <Typography variant="h3-semiBold">You're already in!</Typography>
                        <Typography variant="body01-regular" color="neutral.clr-600">
                            <span className={classes.statusEmail}>{submittedEmail}</span> has
                            already claimed a free Case Master case. Log in to continue where
                            you left off.
                        </Typography>
                        {INTERVIEW_URL && (
                            <CustomLoadingButton
                                onClick={goToLogin}
                                type="button"
                                className={classes.submitButton}
                                style={{ width: '100%' }}
                            >
                                Go to login
                            </CustomLoadingButton>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
